import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";

import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";

import Scheduler from "../utils/Scheduler";
import NextJobForm from "../utils/NextJobForm";
import { showToast } from "../../../../store/actions";
import { useCronReducer } from "../../../../util/cron";
import WorkspaceService from "../../../../service/workspace";
import { ButtonUI, ConfirmPopup } from "../../../Common";
import { FormTile } from "../../../Common/Tile";
import { regexName } from "../../../../util/regularExpress";
import CommonLayout from "../../../Common/Layout";
import KMKGroupSelect from "../../../Common/KMKGroupSelect";
import ComponentSuspense from "../../../Common/ComponentSuspense";
import HelpContextIcon from "../../../Common/HelpPanel/HelpContextIcon";
import { DETAUL_CRON_VALUE, JOB_BY_TYPE } from "../utils/JobData";
import RunJobAsComponent from "../utils/RunJobAsComponent";
import { navScheduledJobs } from "../../../kmk/AppData";

import { arrangeSchema, checkIsClusterExist, checkIsSchemaExist, setPrevState } from "../../../../util/common";

import DataPipelineService from "../../../../service/dataPipelineService";
import SmallToolbar from "../../../Common/SmallToolbar";
import CloneJob from "../CloneJob";

const MyAceEditor = React.lazy(() => import("../../../Common/AceEditor"));

const useStyles = makeStyles()((theme) => ({
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  BigTileContainer: {
    paddingTop: "0px !important",
    marginTop: "10px !important",
  },
  subContainer: {
    display: "flex",
    padding: 0,
    borderTop: 0,
    width: "100%",
    flexDirection: "column",
  },
  PanelContainer: {
    padding: "22px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "250px",
    marginLeft: "inherit",
    "& .MuiPaper-root": {
      height: "auto",
    },
  },
  queryArea: {
    width: "100% !important",
    height: "173px !important",
    border: "1px solid",
    fontSize: "0.7rem",
    "&::selection": { background: "#B9B9B9", color: "#000000" },
    "&::-moz-selection": { background: "#B9B9B9", color: "#000000" },
    "&::-webkit-selection": { background: "#B9B9B9", color: "#000000" },
  },
  AutoComplete: {
    "& .MuiAutocomplete-listbox": {
      maxWidth: "100%",
      wordBreak: "break-all !important",
    },
  },
}));

const CustomPaper = React.memo((props) => {
  return (
    <Paper
      {...props}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxWidth: "100%",
          wordBreak: "break-all !important",
        },
      }}
    />
  );
});

const Types = [{ value: "SQL", text: "SQL" }];

export default function DataTransformScheduler() {
  const { Workspace_id, Job_id } = useParams();
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector((state) => state.user.currentUser);
  const workspace = useSelector((state) => state.workspace);
  const config = useSelector((state) => state.config);
  const clusterList = useSelector((state) => state.clusterList);
  const { defaultSchema, defaultCluster } = config;
  const { Name: workspaceName, IsDataVaultEnabled: isDataVaultEnabled, accessRole, DBName } = workspace.workspace;

  const [locations, setLocations] = useState([]);

  // For Schedule Panel
  const [values, dispatchValues] = useCronReducer("");

  const [transformJobState, setTransformJobState] = useState({
    loading: false,
    jobName: "",
    DataPipeline_id: "",
    Type: "SQL",
    query: "",
    timeZone: "",
    nextRunTime: "",
    scheduleEnabled: "",
    runJobAsUserId: "",
    runJobAsUserFullName: "",
    queryLastUpdatedByName: "",
    database: "",
    schema: "",
    location: "SNOWFLAKE",
    isEntryJob: false,
  });

  const [dataPipelineList, setDataPipelineList] = useState([]);
  // Adding only query for now as it is the only property of which we are require previous state
  const [prevTransformJobState, setPrevTransformJobState] = useState({
    runJobAsUserId: "",
    query: "",
  });

  const [participantsExcludeVisitor, setParticipantsExcludeVisitor] = useState([]);
  const [userNotExist, setUserNotExist] = useState(false);

  const [deleteJobState, setDeleteJobState] = useState({
    openConfirm: false,
    confirmMessage: "",
    loadingForConfirm: false,
  });

  const [jobReassignment, setJobReassignment] = useState({
    openConfirm: false,
    confirmMessage: "",
    loadingForConfirm: false,
  });

  const [extSnowflakeClusterList, setExtSnowflakeClusterList] = useState([]);
  const [computeState, setComputeState] = useState({
    cluster: defaultCluster,
  });

  const [nextJobState, setNextJobState] = useState({
    nextJobToggle: false,
    nextDataPipelineId: "",
    nextJobName: "",
    nextJobId: "",
    activeSchedule: "",
    errorDataPipelineId: "",
    errorJobId: "",
    errorSchedule: "",
  });

  const [openClone, setOpenClone] = useState(false);
  const [schemas, setSchemas] = useState([]);
  const [schemaError, setSchemaError] = useState(false);
  const [clusterError, setClusterError] = useState(false);

  const queryRef = useRef(null);
  const nextJobIdRef = useRef(nextJobState.nextJobId);
  const errorJobIdRef = useRef(nextJobState.errorJobId);
  const textRef = useRef(null);

  const currentUserFullName = useMemo(() => `${user.FirstName} ${user.LastName}`, [user.FirstName, user.LastName]);

  const locationType = useMemo(
    () => locations.find(({ value }) => value === transformJobState.location)?.type || "",
    [locations, transformJobState.location]
  );

  const connectionClusters = useMemo(() => {
    if (locationType === "snowflake") {
      if (transformJobState.location === "SNOWFLAKE") return clusterList;
      return extSnowflakeClusterList;
    }
    return [];
  }, [clusterList, extSnowflakeClusterList, locationType, transformJobState.location]);

  const disableSaveBtn = useMemo(() => {
    if (transformJobState.loading) return true;
    if (Job_id && transformJobState.location === "SNOWFLAKE" && userNotExist) return true;
    if (transformJobState.jobName === "") return true;
    if (!transformJobState.Type) return true;
    if (transformJobState.query === "") return true;
    if (locationType === "snowflake") {
      if (!computeState.cluster) return true;
      if (transformJobState.location === "SNOWFLAKE" && transformJobState.runJobAsUserId === "") return true;
      if (transformJobState.database === "" || transformJobState.schema === "") return true;
    }
    if (nextJobState.nextJobToggle === true && !(nextJobState.nextJobId || nextJobState.errorJobId)) return true;

    return false;
  }, [
    transformJobState.loading,
    transformJobState.location,
    transformJobState.jobName,
    transformJobState.Type,
    transformJobState.query,
    transformJobState.runJobAsUserId,
    transformJobState.database,
    transformJobState.schema,
    Job_id,
    userNotExist,
    locationType,
    nextJobState.nextJobToggle,
    nextJobState.nextJobId,
    nextJobState.errorJobId,
    computeState.cluster,
  ]);

  const handleCancel = (setState, stateVar) => {
    setPrevState(setState, { [stateVar]: false });
  };

  const deleteTransformJob = async () => {
    setPrevState(setDeleteJobState, { loadingForConfirm: true });

    const wsService = new WorkspaceService();
    const result = await wsService.deleteTransformJob(Workspace_id, Job_id);

    if (result.success) {
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }
    setPrevState(setDeleteJobState, { openConfirm: false, loadingForConfirm: false });
  };

  const compareQuery = (oldQuery, newQuery) => {
    oldQuery = oldQuery.trim().toLowerCase();
    newQuery = newQuery.trim().toLowerCase();
    return oldQuery !== newQuery;
  };

  // to get config for save based on location for mysql, external snowflake and current workspace
  const getLocationConfig = useCallback(() => {
    // if location type is mysql
    if (locationType !== "snowflake") return {};

    const baseConfig = {
      cluster: computeState.cluster,
      database: transformJobState.database,
      schema: transformJobState.schema,
    };

    // if location type is external snowflake
    if (transformJobState.location !== "SNOWFLAKE") return baseConfig;

    const userConfig =
      accessRole === 1
        ? {
            runJobAsUserId: transformJobState.runJobAsUserId,
            runJobAsUserFullName: transformJobState.runJobAsUserFullName,
          }
        : compareQuery(prevTransformJobState.query, transformJobState.query)
        ? {
            runJobAsUserId: user.User_id,
            runJobAsUserFullName: currentUserFullName,
          }
        : {
            runJobAsUserId: transformJobState.runJobAsUserId,
            runJobAsUserFullName: transformJobState.runJobAsUserFullName,
          };

    // if location type is current workspace snowflake
    return { ...baseConfig, ...userConfig };
  }, [
    accessRole,
    computeState.cluster,
    currentUserFullName,
    locationType,
    prevTransformJobState.query,
    transformJobState.database,
    transformJobState.location,
    transformJobState.query,
    transformJobState.runJobAsUserFullName,
    transformJobState.runJobAsUserId,
    transformJobState.schema,
    user.User_id,
  ]);

  const handleSave = async (e, clone = false) => {
    // If popup is open then just close it first
    if (jobReassignment.openConfirm) handleCancel(setJobReassignment, "openConfirm");

    setPrevState(setTransformJobState, { loading: true });

    let response = null;
    const submitData = {
      name: transformJobState.jobName.trim(),
      Type: transformJobState.Type,
      location: transformJobState.location,
      DataPipeline_id: transformJobState.DataPipeline_id,
      query: transformJobState.query,
      schedule: values.cronValue || DETAUL_CRON_VALUE,
      scheduleEnabled: transformJobState.scheduleEnabled || false,
      enableNextJob: nextJobState.nextJobToggle,
      ...(nextJobState.nextJobToggle
        ? {
            nextPipeline: nextJobState.nextDataPipelineId,
            nextJob: nextJobState.nextJobId,
            errorPipeline: nextJobState.errorDataPipelineId,
            errorJob: nextJobState.errorJobId,
            disableNextJob: nextJobState.activeSchedule,
            disableErrorJob: nextJobState.errorSchedule,
          }
        : {
            nextPipeline: null,
            nextJob: null,
            errorPipeline: null,
            errorJob: null,
            disableNextJob: null,
            disableErrorJob: null,
          }),
      ...getLocationConfig(),
      queryLastUpdatedByName: !Job_id
        ? currentUserFullName
        : compareQuery(prevTransformJobState.query, transformJobState.query)
        ? currentUserFullName
        : transformJobState.queryLastUpdatedByName,
      isEntryJob: transformJobState.isEntryJob,
    };

    const wsService = new WorkspaceService();
    if (!Job_id) response = await wsService.createTransformJob(submitData, Workspace_id);
    else response = await wsService.updateTransformJob(submitData, Workspace_id, Job_id);

    if (response.success) {
      if (clone) {
        setPrevState(setTransformJobState, { loading: false });
        return true;
      }

      dispatch(showToast({ message: response.message, type: "success" }));
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }

    setPrevState(setTransformJobState, { loading: false });
  };

  const handleCancelClick = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
  }, [Workspace_id, navigate]);

  const handleSchemaChange = (e, value) => {
    setPrevState(setTransformJobState, { schema: value?.title || "", database: value?.groupBy || "" });
    setSchemaError((prevState) => (prevState ? !prevState : prevState));
  };

  const handleUserChange = useCallback(
    (e, { runJobAsUserId, runJobAsUserFullName }) => {
      setPrevState(setTransformJobState, { runJobAsUserId, runJobAsUserFullName });
      setUserNotExist(false);
    },
    [setUserNotExist]
  );

  const getSchemaList = useCallback(
    async (location = "SNOWFLAKE") => {
      const wsService = new WorkspaceService();
      let response;

      if (location === "SNOWFLAKE") {
        response = await wsService.getSchemaListByUser();
      } else {
        response = await wsService.getExternalSnowflakeSchemas(Workspace_id, location, {
          schemasOnly: true,
        });
      }

      const { success, data } = response;

      if (!success) return {};
      const schemas = arrangeSchema(data, setSchemas);
      return schemas;
    },
    [Workspace_id]
  );

  const getExternalSnowflakeCluster = useCallback(
    async (location) => {
      if (!location || location === "SNOWFLAKE") return [];
      const wsService = new WorkspaceService();
      const { success, data } = await wsService.getExternalSnowflakeCluster(Workspace_id, location);
      if (success && data?.length) {
        const clusters = data?.map((cluster) => ({ value: cluster?.name || "" }));
        setExtSnowflakeClusterList(clusters);
        return clusters;
      }
      setExtSnowflakeClusterList([]);
      return [];
    },
    [Workspace_id]
  );

  const handleConnectionChange = useCallback(
    async (location) => {
      setPrevState(setTransformJobState, { loading: true, location });

      const locationType = locations?.find(({ value }) => value === location)?.type || "";

      if (locationType === "snowflake") {
        const [schemasByDB, clusters] = await Promise.all([
          getSchemaList(location),
          location !== "SNOWFLAKE" ? getExternalSnowflakeCluster(location) : clusterList,
        ]);

        const isSchemaExist = checkIsSchemaExist(schemasByDB, transformJobState.database, transformJobState.schema);

        if (!isSchemaExist)
          setPrevState(setTransformJobState, {
            ...(location !== "SNOWFLAKE" ? { database: "", schema: "" } : { database: DBName, schema: defaultSchema }),
          });

        const $defauleCluster = location === "SNOWFLAKE" ? defaultCluster : "";
        const selectedCuster = computeState.cluster || $defauleCluster;
        const isClusterExist = checkIsClusterExist(clusters, selectedCuster);

        setClusterError(!isClusterExist);
        setPrevState(setComputeState, {
          cluster: isClusterExist ? selectedCuster : "",
        });
      }

      setPrevState(setTransformJobState, { loading: false });
    },
    [
      DBName,
      clusterList,
      computeState.cluster,
      defaultCluster,
      defaultSchema,
      getExternalSnowflakeCluster,
      getSchemaList,
      transformJobState.database,
      locations,
      transformJobState.schema,
    ]
  );

  useEffect(() => {
    if (![1, 2].includes(accessRole)) {
      dispatch(
        showToast({
          message: "You do not have permission to perform this action!",
          type: "error",
        })
      );
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }
  }, [Workspace_id, accessRole, dispatch, navigate]);

  useEffect(() => {
    //Get Location List for "Connection" Dropdown
    const getLocationList = async () => {
      const wsService = new WorkspaceService();
      const locations = await wsService.getLocationList(Workspace_id);

      return locations
        .filter((item) => ["mysql", "snowflake"].includes(item.Detail.type.toLowerCase()))
        .map(({ Stage_id, Name, Detail }) => ({ value: Stage_id, text: Name, type: Detail?.type || "" }));
    };

    const getDataTransformSchedule = async (locations) => {
      const wsService = new WorkspaceService();
      const response = await wsService.getDataTransformSchedule(Workspace_id, Job_id);
      if (response === "Unauthorized") return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

      if (response.success) {
        dispatchValues({
          type: "set_values",
          value: response.data.Schedule,
        });

        setPrevState(setNextJobState, {
          nextDataPipelineId: response.data.NextPipeline_id || "",
          nextJobId: response.data.NextJob_id || nextJobIdRef.current,
          errorDataPipelineId: response.data.ErrorPipeline_id || "",
          errorJobId: response.data.ErrorJob_id || errorJobIdRef.current,
          nextJobToggle: response.data.enableNextJob || false,
        });

        setPrevState(setPrevTransformJobState, {
          runJobAsUserId: response.data.Detail.runJobAsUserId,
          query: response.data.Detail.query,
        });

        const location = response.data.Detail.location;
        const selectedLocation = locations.find(({ value }) => value === location);
        const isLocationSnowflake = selectedLocation?.type === "snowflake";
        const clusters = await (isLocationSnowflake
          ? getExternalSnowflakeCluster(selectedLocation?.value)
          : clusterList);
        const isClusterExist = checkIsClusterExist(
          clusters || [],
          response.data?.Detail?.Cluster || (isLocationSnowflake ? defaultCluster : "")
        );
        if (!isClusterExist) setClusterError(true);
        setPrevState(setComputeState, {
          cluster: isClusterExist ? response.data.Detail.Cluster : isLocationSnowflake ? defaultCluster : "",
        });

        return {
          jobName: response.data.Name,
          Type: response.data.Detail.Type || "SQL",
          location: selectedLocation?.value || "SNOWFLAKE",
          DataPipeline_id: response.data.DataPipeline_id,
          query: response.data.Detail.query,
          timeZone: response.data.ServerTimeZone,
          scheduleEnabled: response.data.Enabled,
          nextRunTime: response.data.nextRun,
          runJobAsUserId: response.data.Detail.runJobAsUserId,
          runJobAsUserFullName: response.data.Detail.runJobAsUserFullName,
          queryLastUpdatedByName: response.data.Detail.queryLastUpdatedByName,
          database: response.data.Detail.database,
          schema: response.data.Detail.schema,
        };
      } else return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    };
    const getWorkspaceParticipants = async () => {
      const wsService = new WorkspaceService();
      const { data, success } = await wsService.getWorkspaceParticipants(Workspace_id, [1, 2]);
      return success ? data : [];
    };

    const getTimezone = async () => {
      const wsService = new WorkspaceService();
      const result = await wsService.getTimezone(Workspace_id);
      return result.data;
    };

    const getDataPipelineList = async () => {
      const pipeline = new DataPipelineService();
      const response = await pipeline.getDataPipelineList(Workspace_id);
      if (response.success && response.data?.dataPipelineList?.length) return response.data.dataPipelineList;
      return [];
    };

    async function fetchData() {
      if (clusterList === false) return;
      setPrevState(setTransformJobState, { loading: true });

      let initialState = {};

      const [locations, dataPipelines, workspaceParticipatns] = await Promise.all([
        getLocationList(),
        getDataPipelineList(),
        getWorkspaceParticipants(),
      ]);

      // no need to await this fetched schema list is for create job and will not be of use in this fun
      if (!Job_id) getSchemaList();

      setParticipantsExcludeVisitor(workspaceParticipatns);

      // Adding entry for current workspace option
      const snowflakeLocation = {
        value: "SNOWFLAKE",
        text: workspaceName + " (Current Workspace DB)",
        type: "snowflake",
      };

      setLocations([snowflakeLocation, ...locations]);

      if (Job_id) {
        initialState = await getDataTransformSchedule(locations);

        const selectedLocationType =
          locations.find(({ value }) => value === initialState?.location)?.type || "snowflake";

        if (selectedLocationType === "snowflake") {
          const schemasByDB = await getSchemaList(initialState.location);
          const isSchemaExist = checkIsSchemaExist(schemasByDB, initialState.database, initialState.schema);
          if (!isSchemaExist) {
            setSchemaError(true);
            initialState.database = "";
            initialState.schema = "";
          }

          if (initialState.location === "SNOWFLAKE")
            setUserNotExist(
              !workspaceParticipatns.some((_user) => {
                return _user.User_id === initialState.runJobAsUserId;
              })
            );
        }
      } else {
        initialState.timeZone = await getTimezone();
        initialState.runJobAsUserId = user.User_id;
        initialState.runJobAsUserFullName = currentUserFullName;
        initialState.database = DBName;
        initialState.schema = defaultSchema;
      }

      if (!initialState.DataPipeline_id)
        initialState.DataPipeline_id = dataPipelines?.find((pipeline) => pipeline.IsDefault === 1)?.DataPipeline_id;
      setDataPipelineList(dataPipelines);

      if (!initialState.runJobAsUserId) {
        initialState.runJobAsUserId = user.User_id;
        initialState.runJobAsUserFullName = currentUserFullName;
      }

      let isEntryJob = false;
      if (Job_id) {
        const currentPipeline = dataPipelines.find(
          ({ DataPipeline_id }) => parseInt(DataPipeline_id) === parseInt(initialState.DataPipeline_id)
        );
        isEntryJob = currentPipeline.EntryJob_id === parseInt(Job_id);
      }

      setPrevState(setTransformJobState, {
        ...initialState,
        loading: false,
        isEntryJob,
      });
    }
    fetchData();
  }, [
    DBName,
    Job_id,
    Workspace_id,
    clusterList,
    currentUserFullName,
    defaultCluster,
    defaultSchema,
    dispatchValues,
    getExternalSnowflakeCluster,
    getSchemaList,
    navigate,
    user.User_id,
    workspaceName,
  ]);

  /**
   * Alpha Numeric Characters only with Space allow (first letter should Alpha)
   * @param {*} str
   * @returns
   */
  const handleJobName = (str, dataState) => {
    const isValid = regexName(str, "jobName", "_ -");
    if (isValid) setPrevState(setTransformJobState, { [dataState]: str });
  };

  const resetNextJob = () =>
    setPrevState(setNextJobState, {
      activeSchedule: "",
      nextJobToggle: false,
      nextJobName: "",
      nextJobId: "",
      errorJobId: "",
      errorSchedule: "",
    });

  const confirmPipelineChange = useCallback((e) => {
    setTransformJobState((prev) => ({
      ...prev,
      DataPipeline_id: e.target.value,
      ...(prev.DataPipeline_id ? { isEntryJob: false } : {}),
    }));
    resetNextJob();
  }, []);

  const addJobForm = () => {
    return (
      <Grid container>
        <Grid container direction={"row"} spacing={2} sx={{ mb: "16px" }}>
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <TextField
              value={transformJobState.jobName}
              fullWidth
              required
              label="Job Name"
              inputProps={{ maxLength: 30 }}
              onChange={(e) => handleJobName(e.target.value, "jobName")}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            {!!isDataVaultEnabled && (
              <FormControl variant="outlined" required fullWidth>
                <InputLabel>Pipeline</InputLabel>
                <Select
                  value={transformJobState.DataPipeline_id}
                  onChange={confirmPipelineChange}
                  label="Pipeline"
                  disabled={!!Job_id}
                  MenuProps={{ PaperProps: { style: { maxHeight: "300px", maxWidth: "300px" } } }}
                  renderValue={(selected) => {
                    const selectedPipeline = dataPipelineList.find((p) => p.DataPipeline_id === selected);
                    const isOverflow =
                      (textRef.current?.scrollWidth > textRef.current?.clientWidth || false) && !!Job_id;

                    return (
                      <Stack direction="row" alignItems="center">
                        <Tooltip
                          placement="bottom"
                          title={selectedPipeline?.Name}
                          arrow
                          disableHoverListener={!isOverflow}
                        >
                          <Typography
                            variant="body1"
                            ref={textRef}
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "300px",
                            }}
                          >
                            {selectedPipeline?.Name || "Select a pipeline"}
                          </Typography>
                        </Tooltip>
                        {selectedPipeline?.IsDefault === 1 && (
                          <Tooltip placement="right" title="Default Pipeline" arrow>
                            <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                          </Tooltip>
                        )}
                      </Stack>
                    );
                  }}
                >
                  {dataPipelineList.map((p) => (
                    <MenuItem
                      key={p.DataPipeline_id}
                      value={p.DataPipeline_id}
                      style={{ whiteSpace: "normal", wordWrap: "break-word", wordBreak: "break-all" }}
                    >
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="body1" sx={{ m: 0, p: 0 }}>
                          {p.Name}
                        </Typography>
                        {p.IsDefault === 1 && (
                          <Tooltip placement="right" title="Default Pipeline" arrow>
                            <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                          </Tooltip>
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid sx={{ display: "flex", alignItems: "center" }}>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox />}
                value={transformJobState.isEntryJob}
                checked={transformJobState.isEntryJob}
                onChange={() => setTransformJobState((prev) => ({ ...prev, isEntryJob: !prev.isEntryJob }))}
                label="Set as the entry point for the pipeline"
              />
            </FormGroup>
          </Grid>
        </Grid>
        <Grid container direction={"row"} spacing={2} sx={{ mb: "16px" }}>
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <FormControl fullWidth>
              <InputLabel required>Connection</InputLabel>
              <Select
                value={transformJobState.location}
                label="Connection"
                required
                MenuProps={{ sx: { height: "220px" } }}
                onChange={(e) => handleConnectionChange(e.target.value)}
              >
                {locations.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4,
            }}
          >
            <FormControl fullWidth>
              <InputLabel required>Type</InputLabel>
              <Select
                value={transformJobState.Type}
                label="Type"
                required
                MenuProps={{ sx: { height: "220px" } }}
                onChange={(e) => {
                  setPrevState(setTransformJobState, { Type: e.target.value });
                }}
              >
                {Types.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.text}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        {transformJobState.location === "SNOWFLAKE" && (
          <Grid container direction={"row"} spacing={2} sx={{ mb: "16px" }}>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <RunJobAsComponent
                participants={participantsExcludeVisitor}
                selectedUser={transformJobState.runJobAsUserId}
                handleChange={handleUserChange}
                handleRunUnderMyName={(e) =>
                  handleUserChange(e, { runJobAsUserId: user.User_id, runJobAsUserFullName: currentUserFullName })
                }
                error={Job_id && userNotExist}
                editable={accessRole === 1 && participantsExcludeVisitor.length > 0}
                nonEditableValue={userNotExist ? "" : transformJobState.runJobAsUserFullName}
                fullWidth
              />
            </Grid>
          </Grid>
        )}
        {locationType === "snowflake" && (
          <Grid container direction={"row"} spacing={2}>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormControl required fullWidth variant="outlined">
                <InputLabel>Compute Cluster</InputLabel>
                <Select
                  value={computeState.cluster}
                  onChange={(e) => setPrevState(setComputeState, { cluster: e.target.value })}
                  label="Compute Cluster"
                  MenuProps={{ sx: { height: "220px" } }}
                  error={clusterError}
                  errorText={"Cluster does not exists or you do not have access to work on it."}
                >
                  {connectionClusters?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <KMKGroupSelect
                options={schemas}
                label="Schema"
                value={{ title: transformJobState.schema, groupBy: transformJobState.database }}
                handleChange={handleSchemaChange}
                error={schemaError}
                errorText={"Schema does not exists or you do not have access to work on it."}
                noOptionsText={"No schema found"}
                className={classes.AutoComplete}
                PaperComponent={CustomPaper}
                fullWidth
                required
              />
            </Grid>
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            sm: 4,
          }}
        ></Grid>
        <Grid sx={{ paddingTop: "10px !important", display: "flex" }} size={12}>
          <Typography color={"#00000099"} padding={0} variant="subtitle2" component="span">
            {Job_id ? `Query * (Last updated by: ${transformJobState.queryLastUpdatedByName || "-"})` : `Query *`}
          </Typography>
        </Grid>
        <Grid sx={{ paddingTop: "2px !important" }} size={12}>
          <ComponentSuspense>
            <MyAceEditor
              fontSize={14}
              style={{ paddingTop: "10px" }}
              className={classes.queryArea}
              ref={queryRef}
              label="Query"
              value={transformJobState.query}
              setQuery={(value) => setPrevState(setTransformJobState, { query: value })}
              location={transformJobState.location}
              type={locationType?.toLowerCase() || ""}
              runParams={{
                runJobAsUserId: Job_id && userNotExist ? null : transformJobState.runJobAsUserId,
                database: transformJobState.database,
                schema: transformJobState.schema,
                cluster: computeState.cluster,
              }}
              queryRestriction={{ onlyOneQuery: false, onlySelectQuery: false }}
              queryLastUpdatedByName={transformJobState.queryLastUpdatedByName}
            />
          </ComponentSuspense>
        </Grid>
      </Grid>
    );
  };

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    {
      link: `/my-workspaces/${Workspace_id}`,
      displayName: workspaceName,
      active: false,
    },
    {
      link: `/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`,
      displayName: "Data Pipelines",
      active: false,
    },
    { link: "#", displayName: `${Job_id ? "Edit Data Transform Job" : "Add Data Transform Job"}`, active: true },
  ];

  const toggleSchedule = useCallback(
    () => setTransformJobState((prev) => ({ ...prev, scheduleEnabled: !prev.scheduleEnabled })),
    []
  );

  return (
    <CommonLayout title="Job Scheduler" loading={transformJobState.loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          overflowX: "hidden",
        }}
        id="main_box"
      >
        <Backdrop
          sx={{ background: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={transformJobState.loading}
        >
          <CircularProgress color="primary" />
        </Backdrop>
        <Box maxWidth="lg" sx={{ mb: 4, mx: 0 }}>
          <Box className={classes.subContainer}>
            <Box className={classes.PanelContainer}>
              <Grid container direction="row" sx={{ mb: 4 }}>
                <Grid sx={{ mb: 1, display: "flex", alignItems: "center" }} size={12}>
                  <Typography className={classes.HeaderTitle} component="div">
                    {`${Job_id ? "Edit Data Transform Job" : "Add Data Transform Job"}`}
                  </Typography>
                  <ButtonUI
                    variant="contained"
                    color="success"
                    disabled={disableSaveBtn}
                    onClick={() => {
                      if (
                        accessRole === 2 &&
                        Job_id &&
                        compareQuery(prevTransformJobState.query, transformJobState.query) &&
                        transformJobState.runJobAsUserId !== user.User_id
                      ) {
                        setPrevState(setJobReassignment, { openConfirm: true });
                      } else {
                        handleSave();
                      }
                    }}
                  >
                    Save
                  </ButtonUI>
                  <ButtonUI onClick={handleCancelClick} variant="contained" color="secondary" sx={{ ml: "15px" }}>
                    Cancel
                  </ButtonUI>

                  {Job_id ? (
                    <>
                      <ButtonUI
                        onClick={() => setOpenClone(true)}
                        variant="contained"
                        color="info"
                        sx={{ mx: "15px" }}
                        disabled={disableSaveBtn}
                      >
                        Clone
                      </ButtonUI>

                      <ButtonUI
                        variant="contained"
                        color="error"
                        onClick={() => {
                          setPrevState(setDeleteJobState, {
                            openConfirm: true,
                            confirmMessage: `Are you sure you want to delete "${transformJobState.jobName}" Job?"`,
                          });
                        }}
                        disabled={!Job_id}
                      >
                        Delete
                      </ButtonUI>
                    </>
                  ) : (
                    <></>
                  )}

                  <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                    <HelpContextIcon screen="Data Transform Job" />
                  </Box>
                </Grid>

                <Grid className={classes.BigTileContainer} size={12}>
                  <FormTile>{addJobForm()}</FormTile>
                </Grid>
                <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                  <SmallToolbar>Schedule</SmallToolbar>
                  <FormTile
                    style={{
                      overflowX: "auto",
                    }}
                  >
                    <Scheduler
                      scheduleEnabled={transformJobState.scheduleEnabled}
                      cronValue={values.cronValue}
                      dispatchValues={dispatchValues}
                      toggleValue={toggleSchedule}
                      timeZone={transformJobState.timeZone}
                      nextRunTime={transformJobState.nextRunTime}
                    />
                  </FormTile>
                </Grid>
                <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                  <SmallToolbar>Next Job</SmallToolbar>
                  <FormTile>
                    <NextJobForm
                      dataPipelineList={dataPipelineList}
                      nextJobToggle={nextJobState.nextJobToggle}
                      nextDataPipelineId={nextJobState.nextDataPipelineId}
                      nextJobId={nextJobState.nextJobId}
                      errorDataPipelineId={nextJobState.errorDataPipelineId}
                      errorJobId={nextJobState.errorJobId}
                      setNextJobState={setNextJobState}
                    />
                  </FormTile>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>

      {openClone && (
        <CloneJob
          setOpenClone={setOpenClone}
          pipelineId={transformJobState.DataPipeline_id}
          handleSave={handleSave}
          JobType={JOB_BY_TYPE.dataTransform}
        />
      )}

      {deleteJobState.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: "Delete Data Transform Job",
            popupMessage: deleteJobState.confirmMessage,
            handleCancel: () => handleCancel(setDeleteJobState, "openConfirm"),
            handleClick: deleteTransformJob,
            loadingForConfirm: deleteJobState.loadingForConfirm,
          }}
        />
      )}
      {jobReassignment.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: "Job Reassignment",
            popupMessage: (
              <>
                <span>
                  You have edited the query for this job. Saving the job will now result in running the job under your
                  name.
                </span>
                <br />
                <span>Are you sure you want to proceed?</span>
              </>
            ),
            handleCancel: () => handleCancel(setJobReassignment, "openConfirm"),
            handleClick: handleSave,
          }}
        />
      )}
    </CommonLayout>
  );
}
